import React, { ElementType, FC } from 'react';

import { Descendant } from 'slate';

// components
import { FormValues } from '../components/formulas/settings/FormulaTags/helper';
import { SearchableSelectType } from '../components/inputs/searchableSelect';

// constants
import aampeTheme from '../constants/aampeTheme';
import { FormulaTestMessageType } from '../constants/formula';
import { FormulaType } from '../constants/formulaType';
import ICONS from '../constants/icons';

import ContentCardsPreview from '../views/formulaCreate/messagePreview/contentCards/ContentCardsPreview';
import EmailPreview from '../views/formulaCreate/messagePreview/email/EmailMain';
import MobilePushPreview from '../views/formulaCreate/messagePreview/mobilePush/MobilePushMain';
import SmsPreview from '../views/formulaCreate/messagePreview/sms/SmsMain';
import WebPushPreview from '../views/formulaCreate/messagePreview/webPush/WebPushMain';
import WhatsappPreview from '../views/formulaCreate/messagePreview/whatsapp/WhatsappMain';
import {
    TestFormulaSectionType,
    TestMessageStep,
} from 'views/formulaCreate/formulaEditor/assistant/testFormula/constant';

// models
import {
    CMSFilterCreateModel,
    CMSFilterModel,
    CMSFilterType,
    CMSType,
} from './cms-placeholder.model';
import { CompoundTriggerModel } from './compound-trigger.model';
import { AttributeOperatorModel } from './contact_attributes.model';
import { DatasetFiltersModel } from './dataset-filters.model';
import { ElementKind, SectionType } from './editor.model';
import { GoalBaseModel } from './goal.model';
import { TestMessageFilter } from './message-content.model';
import { IosCategoryIdentifier } from './notificationButton.model';
import { TagModel } from './tag.model';
import { CustomerId } from './user.model';
import { VariantModelV1 } from './variant.model';

export enum FormulaPreviewCount {
    default = 10,
    max = 100,
}
export interface ChannelTypeModel {
    push: string;
    web_push: string;
    sms: string;
    whatsapp: string;
    in_app_banner: string;
    in_app_fullpage: string;
    email: string;
    surface: string;
}

export interface FormulaAdditionalMetadata {
    email_template_id?: string;
    from_?: string;
    is_aampe_custom_template?: boolean;
}

export interface FormulaBaseModel {
    id: number;
    name: string;
    message_count: number;
    message_type: FormulaType;
    status: FormulaStatus;
    structure?: Descendant[];
    created_at: string;
    updated_at: string;
    tags?: TagModel[];
    surface_id?: string;
    has_write_access?: boolean;
    topic_id?: string;
    additional_metadata?: FormulaAdditionalMetadata;
}

export interface FormulaModel extends FormulaBaseModel {
    active_state: FormulaState;
    send_start: string;
    send_stop: string;
    send_start_date?: string;
    send_stop_date?: string;
    deeplink: string;
    deeplink_dataset_field_id: string;
    deeplink_config: Record<string, string>;
    dataset_id: number;
    days_live?: number;
    dataset_filters?: DatasetFiltersModel;
    compound_trigger?: CompoundTriggerModel;
    is_labeled_matching_enabled?: boolean;
}

export interface FormulaAudienceIdModel {
    audience_id?: string;
}
export interface FormulaRewardFunctionModel {
    reward_function_id?: string;
}
export interface FormulaDraftPayloadModel {
    is_in_edit_mode?: boolean;
}
export interface FormulaDetailModel extends FormulaModel {
    approved_at?: string;
    approved_by_id?: number;
    archived_at?: string;
    archived_by_id?: number;
    review_request_at?: string;
    review_request_by_id?: number;
    goals?: GoalBaseModel[];
    labels?: FormulaLabel[];
    content_freshness_score?: number;
    is_controlled_message?: boolean;
    cms_placeholder_filters?: CMSFilterModel[];
    audiences?: FormulaAudienceIdModel[];
    reward_function_id?: string;
    override_provider_frequency_limits?: boolean;
    cms_type?: CMSType;
    time_slot_ids?: string[];
    timezone?: string;
    is_in_edit_mode?: boolean;
    additional_metadata?: FormulaAdditionalMetadata;
    content_card_type?: ContentCardType;
    ios_notification_category?: IosCategoryIdentifier;
    alternate_labels_count?: number;
    send_start_or_send_end?: boolean;
    messageContentId?: string;
    has_write_access?: boolean;
    topic_id?: string;
}

export interface FormulaApproveModelV1 extends FormulaDetailModel {
    components: FormulaComponentModelV1[];
    is_approved: boolean;
}

export interface FormulaComponentModelV1 {
    id: string;
    component_type_id: string;
    variants: VariantModelV1[];
}

export interface ApprovedFormulaCatalog extends FormulaBaseModel {
    active_state: ApprovedFormulaState;
    days_live?: number;
    content_freshness_score?: number;
}

export interface ProcessedFormulaV1 extends FormulaDetailModel {
    components: FormulaComponentModelV1[];
    is_approved: boolean;
}

export interface FormulaLabelCreateModel {
    label_id: number | string;
    variant_type: string;
}

export interface FormulaCreateModel {
    name?: string;
    message_type?: FormulaType;
    structure?: Descendant[];
    compound_trigger_id?: string;
    is_controlled_message?: boolean;
    send_start?: string | null;
    send_start_date?: string | null;
    labels?: FormulaLabelCreateModel[];
    tags?: string[];
    audience_id?: string;
    reward_function_id?: string;
    cms_type?: CMSType | null;
    additional_metadata?: FormulaAdditionalMetadata;
    content_card_type?: ContentCardType;
    formula_sequence_touchpoint_id?: string;
    dataset_id?: number | null;
    surface_id?: string;
    topic_id?: string;
}

export interface FormulaUpdateModel extends FormulaCreateModel {
    deeplink?: string | null;
    deeplink_dataset_field_id?: string | null;
}

export interface FormulaAddLabelModel {
    id: number;
    labels: FormulaLabelCreateModel[];
}

export interface FormulaUpdateBase {
    id: number;
    name?: string;
    send_start?: string | null;
    send_stop?: string | null;
    send_start_date?: string | null;
    send_stop_date?: string | null;
    is_controlled_message?: boolean;
    override_provider_frequency_limits?: boolean;
    audience_id?: string | null;
    reward_function_id?: string | null;
    tags?: string[];
    compound_trigger_id?: string;
    cms_placeholder_filters?: CMSFilterCreateModel[];
    timezone?: string;
    additional_metadata?: FormulaAdditionalMetadata;
    content_card_type?: ContentCardType;
    formula_sequence_touchpoint_id?: string;
    labels?: FormulaLabelCreateModel[];
    email_template_field_mappings?: Record<string, string | null>;
    topic_id?: string | null;
}

export enum FormulaCategory {
    Draft = 'Draft',
    Review = 'Review',
    Scheduled = 'Scheduled',
    Live = 'Live',
    Expired = 'Expired',
    Archived = 'Archived',
}

export enum FormulaStatus {
    Draft = 'Draft',
    Review = 'Review',
    Approved = 'Approved',
    Archived = 'Archived',
}

export enum ApprovedFormulaState {
    Scheduled = 'Scheduled',
    Live = 'Live',
    Expired = 'Expired',
}

export interface FormulaLabel {
    label_id: number;
    label_name: string;
    variant_type: string;
}

export interface FormulaStatusDataModel {
    background: string;
    color: string;
    label: string;
    description?: string;
}

export const FormulaStatusData: {
    [key in FormulaStatus | ApprovedFormulaState]: FormulaStatusDataModel;
} = {
    [FormulaStatus.Draft]: {
        background: aampeTheme.colors.interactiveWG03,
        color: aampeTheme.colors.textAccessibleWG01,
        label: 'Drafts',
        description:
            'These messages are still in “Draft.” None of them will be sent until approved.',
    },
    [FormulaStatus.Review]: {
        background: aampeTheme.colors.secondary50,
        color: aampeTheme.colors.secondary500,
        label: 'Review',
        description:
            'These messages are marked for review (used for organizations that have an approval workflow).',
    },
    [FormulaStatus.Approved]: {
        background: aampeTheme.colors.success50,
        color: aampeTheme.colors.success700,
        label: 'Approved',
    },
    [FormulaStatus.Archived]: {
        background: aampeTheme.colors.primary50,
        color: aampeTheme.colors.solidA01,
        label: 'Archived',
    },
    [ApprovedFormulaState.Live]: {
        background: aampeTheme.colors.success50,
        color: aampeTheme.colors.success700,
        label: 'Live',
        description:
            'These messages are approved and are currently being sent to users.',
    },
    [ApprovedFormulaState.Scheduled]: {
        background: aampeTheme.colors.indigo50,
        color: aampeTheme.colors.blueneutral09,
        label: 'Scheduled',
        description:
            'These messages are approved but will not be sent until a particular date.',
    },
    [ApprovedFormulaState.Expired]: {
        background: aampeTheme.colors.error50,
        color: aampeTheme.colors.error500,
        label: 'Expired',
        description:
            'These messages are approved but were scheduled for sending on dates that have already passed.',
    },
};

export type FormattedAudienceFilters = {
    [key: string]: AudienceFilterCategoryOptions[];
};

export type AudienceFilterCategoryOptions = {
    operator: AttributeOperatorModel;
    value: string;
};

export type AudienceFilterOptionValue = {
    [key: string]: { operatorId: number; value: string }[];
};

export enum FormulaProperty {
    CompoundTrigger = 'compound_trigger',
    Tags = 'tags',
    Audience = 'audience',
}

export interface RewardFunction {
    id: string;
    name: string;
    description: string | null;
    events_count: number;
    formulas_count: number;
    is_default: boolean;
}

export type FormulaState = FormulaStatus | ApprovedFormulaState;

export type TestFormulaContent = Record<SectionType, string>;

export interface TestFormulaModel {
    test_message_content: TestFormulaContent;
}

export type FormulaChannelCountModel = { [key in FormulaType]: number };

export type FormulasCategoryCountModel = {
    [key in FormulaCategory]: FormulaChannelCountModel;
};

export enum TestMessageType {
    Token = 'Token',
    PhoneNumber = 'PhoneNumber',
    Email = 'Email',
}
export enum ContentCardType {
    TextOnly = 'text_only',
    ImageAndText = 'image_and_text',
    ImageOnly = 'image_only',
}

interface GetTemplateProps {
    content_card_type?: ContentCardType;
}

export interface FormulaTypesDataModel {
    key: string;
    isIntegrated?: (...values: any[]) => boolean;
    title: string;
    previewComponent: FC<React.PropsWithChildren<any>>;
    getTemplate: (props?: GetTemplateProps) => Descendant[];
    Icon: ElementType;
    allowButtons?: boolean;
    supportDeeplink?: boolean;
    supportTrigger?: boolean;
    supportAudience?: boolean;
    supportScheduling?: boolean;
    supportContentTag?: boolean;
    supportAampiConnect?: boolean;
    testMessageInfo?: {
        type: TestMessageType;
        label: string;
    };
    supportMultipleViews?: boolean;
    color?: string;
    charWarningLimits?: {
        [key in SectionType]?: number;
    };
    supportTestMessage?: boolean;
    standaloneMessages?: boolean;
}
const ContentCardCommonTemplate: Descendant[] = [
    {
        kind: ElementKind.section,
        sectionType: SectionType.headline,
        children: [{ text: '' }],
    },
    {
        kind: ElementKind.section,
        sectionType: SectionType.body,
        children: [{ text: '' }],
    },
    {
        kind: ElementKind.section,
        sectionType: SectionType.buttonText,
        children: [{ text: '' }],
    },
    {
        kind: ElementKind.section,
        sectionType: SectionType.buttonLink,
        children: [{ text: '' }],
    },
];

export const FormulaTypesData: {
    [key in FormulaType]: FormulaTypesDataModel;
} = {
    [FormulaType.mobilePush]: {
        key: FormulaType.mobilePush,
        isIntegrated: () => true,
        title: 'Mobile push',
        Icon: ICONS.mobilePush,
        previewComponent: MobilePushPreview,
        supportDeeplink: true,
        supportTrigger: true,
        supportScheduling: true,
        testMessageInfo: { type: TestMessageType.Token, label: 'Token' },
        supportMultipleViews: true,
        supportAudience: true,
        supportTestMessage: true,
        standaloneMessages: true,
        getTemplate: () => [
            {
                kind: ElementKind.section,
                sectionType: SectionType.header,
                children: [{ text: '' }],
            },
            {
                kind: ElementKind.section,
                sectionType: SectionType.body,
                children: [{ text: '' }],
            },
            {
                kind: ElementKind.image,
                children: [{ text: '' }],
            },
        ],
        color: aampeTheme.liveMessagesColors.mobilePush,
        charWarningLimits: {
            [SectionType.header]: 40,
        },
    },
    [FormulaType.webPush]: {
        key: FormulaType.webPush,
        title: 'Web push',
        Icon: ICONS.webPush,
        previewComponent: WebPushPreview,
        supportDeeplink: true,
        supportTrigger: true,
        supportScheduling: true,
        supportAudience: true,
        standaloneMessages: true,
        getTemplate: () => [
            {
                kind: ElementKind.section,
                sectionType: SectionType.header,
                children: [{ text: '' }],
            },
            {
                kind: ElementKind.section,
                sectionType: SectionType.body,
                children: [{ text: '' }],
            },
        ],
        color: aampeTheme.liveMessagesColors.webPush,
    },
    [FormulaType.whatsapp]: {
        key: FormulaType.whatsapp,
        title: 'Whatsapp',
        Icon: ICONS.whatsapp,
        isIntegrated: () => true,
        testMessageInfo: {
            type: TestMessageType.PhoneNumber,
            label: 'Phone Number',
        },
        supportTrigger: true,
        supportScheduling: true,
        supportMultipleViews: true,
        previewComponent: WhatsappPreview,
        supportAudience: true,
        supportTestMessage: true,
        standaloneMessages: true,
        getTemplate: () => [
            {
                kind: ElementKind.section,
                sectionType: SectionType.message,
                children: [{ text: '' }],
            },
        ],
        color: aampeTheme.liveMessagesColors.whatsapp,
    },
    [FormulaType.sms]: {
        key: FormulaType.sms,
        isIntegrated: (customerId) =>
            ALLOWED_CUSTOMER_IDS_FOR_SMS_MESSAGE.includes(customerId),
        title: 'SMS',
        Icon: ICONS.sms,
        previewComponent: SmsPreview,
        supportTrigger: true,
        supportScheduling: true,
        supportMultipleViews: true,
        supportAudience: true,
        supportTestMessage: true,
        standaloneMessages: true,
        testMessageInfo: {
            type: TestMessageType.PhoneNumber,
            label: 'Phone Number',
        },
        getTemplate: () => [
            {
                kind: ElementKind.section,
                sectionType: SectionType.message,
                children: [{ text: '' }],
            },
        ],
        color: aampeTheme.liveMessagesColors.sms,
    },
    [FormulaType.email]: {
        key: FormulaType.email,
        title: 'Email',
        Icon: ICONS.email,
        isIntegrated: () => true,
        previewComponent: EmailPreview,
        supportAudience: true,
        supportTrigger: true,
        supportScheduling: true,
        testMessageInfo: { type: TestMessageType.Email, label: 'Email' },
        supportMultipleViews: true,
        supportTestMessage: true,
        standaloneMessages: true,
        getTemplate: () => [
            {
                kind: ElementKind.section,
                sectionType: SectionType.subject,
                children: [{ text: '' }],
            },
        ],
        color: aampeTheme.liveMessagesColors.email,
    },
    [FormulaType.contentCards]: {
        key: FormulaType.contentCards,
        isIntegrated: () => true,
        title: 'Content cards',
        Icon: ICONS.contentCards,
        previewComponent: ContentCardsPreview,
        supportScheduling: false,
        supportAampiConnect: true,
        testMessageInfo: { type: TestMessageType.Token, label: 'Token' },
        supportMultipleViews: true,
        supportTestMessage: false,
        standaloneMessages: false,
        getTemplate: (props) => {
            switch (props?.content_card_type) {
                case ContentCardType.ImageAndText:
                    return [
                        {
                            kind: ElementKind.image,
                            children: [{ text: '' }],
                        },
                        ...ContentCardCommonTemplate,
                    ];
                case ContentCardType.ImageOnly:
                    return [
                        {
                            kind: ElementKind.image,
                            children: [{ text: '' }],
                        },
                        {
                            kind: ElementKind.section,
                            sectionType:
                                SectionType.destination_link_on_image_click,
                            children: [{ text: '' }],
                        },
                    ];
                default:
                    return ContentCardCommonTemplate;
            }
        },
        color: aampeTheme.liveMessagesColors.contentCard,
        charWarningLimits: {
            [SectionType.header]: 40,
        },
    },
    [FormulaType.surface]: {
        key: '',
        title: 'Surface',
        previewComponent: () => null,
        getTemplate: () => [],
        Icon: ICONS.contentCards,
        isIntegrated: () => true,
        standaloneMessages: false,
        color: aampeTheme.liveMessagesColors.surfaces,
    },
};

export interface PreviewComponentProps<T> {
    randomSample: T[];
    formulaHasVariant: boolean;
    isCoverPreview?: boolean;
}

export const ALLOWED_CUSTOMER_IDS_FOR_SMS_MESSAGE: CustomerId[] = [
    CustomerId.Aampe,
    CustomerId.Intelycare,
    CustomerId.PayU,
    CustomerId.JoeCoffee,
];

export interface FormulaLiteModel {
    id: number;
    name?: string;
    active_state: FormulaState;
    message_type: FormulaType;
    message_count: number;
    surface_id?: string;
}

export interface FormulaTabDataModel {
    name: string;
    description?: string;
}

export interface ImageDeviceSpecsModel {
    aspect_ratio: string;
    resolution: string;
    minimum_resolution: string;
    file_types: string;
}

export interface ImageSpecsModel {
    heading: string;
    specs: ImageDeviceSpecsModel;
}

export type FormulaLabelsAlternateCountModel = {
    [key in string]?: Record<string, number>;
};

export type MaxCharacterModel = Partial<
    Record<SectionType, number | undefined>
>;

export type LabelVariantHealthModel = {
    [key in string]?: string;
};

export enum FormulaCloneOptions {
    Audiences = 'audiences',
    CompoundTriggers = 'compound_triggers',
    Tags = 'tags',
    FormulaLabels = 'formula_labels',
    CartReferences = 'cart_references',
}
export enum FormulaCoverSortOptions {
    Header = 'header',
    Body = 'body',
    Random = 'random',
    Message = 'message',
}

export const FormulaCloneOptionLabels = {
    audiences: 'Audiences',
    compound_triggers: 'Triggers',
    tags: 'Tags',
    formula_labels: 'Message Labels',
    cart_references: 'Cart References',
};

export interface FormulaCloneModel {
    formulaId: number;
    data: { clone_options: FormulaCloneOptions[] };
}

export type LabelVariantTypeValue = {
    [key in string]?: string[];
};
export interface FormulaSettingsCommonValues {
    compound_trigger: SearchableSelectType | null;
    audience_id: string;
    reward_function_id?: string;
    cms_type: SearchableSelectType;
    cms_filters: CMSFilterModel[];
    cms_filter_type: CMSFilterType;
    send_start?: string;
    send_start_date?: string;
    send_stop?: string;
    send_stop_date?: string;
    time_slot_ids?: string[];
    is_controlled_message?: boolean;
    timezone?: string;
}

export type FormulaSettingsInitialValueProps = FormValues &
    FormulaSettingsCommonValues;

export enum FormulaViewTabs {
    COVER = 'cover',
    SUMMARY = 'summary',
    ALTERNATES = 'alternates',
    TRAIL = 'trail',
    CONTENT = 'content',
    AAMPE_CONNECT = 'aampe_connect',
}

export const FormulaViewTabsData = [
    {
        key: FormulaViewTabs.COVER,
        name: 'Cover',
    },
    {
        key: FormulaViewTabs.SUMMARY,
        name: 'Summary',
    },
    {
        key: FormulaViewTabs.CONTENT,
        name: 'Content',
    },
    {
        key: FormulaViewTabs.ALTERNATES,
        name: 'Alternates',
        tooltip: 'View all of the unique, sendable messages you’ve created.',
    },
    {
        key: FormulaViewTabs.TRAIL,
        name: 'Trail',
    },
    {
        key: FormulaViewTabs.AAMPE_CONNECT,
        name: 'Aampe Connect',
    },
];

export interface FormulaViewSetupFormValue
    extends FormValues,
        FormulaSettingsCommonValues {
    name?: string;
    labels?: FormulaLabel[];
    scheduling_type?: FormulaSchedulingOptions;
    topic_id?: string | null;
}

export enum FormulaSchedulingOptions {
    AUTOMATED = 'automated',
    AUTOMATED_WITH_GUARDRAILS = 'automated_with_guardrails',
    CONTROLLED = 'controlled',
}

export enum PreviewView {
    ANDROID = 'android',
    IOS = 'ios',
    DESKTOP = 'desktop',
    MOBILE = 'mobile',
}

export const FormulaSchedulingOptionsLabels = {
    [FormulaSchedulingOptions.AUTOMATED]: 'Delegate to Agents',
    [FormulaSchedulingOptions.AUTOMATED_WITH_GUARDRAILS]:
        'Automated scheduling with guardrails',
    [FormulaSchedulingOptions.CONTROLLED]: 'One-at-a-time message scheduling',
};
export const FormulaCardCategories = [
    {
        heading: 'In review',
        description: 'Review your messages and approve them.',
        category: FormulaCategory.Review,
    },
    {
        heading: 'Scheduled',
        description: 'Scheduled messages that start sending this week.',
        category: FormulaCategory.Scheduled,
    },
    {
        heading: 'Expired',
        description: 'Controlled messages that have finished sending.',
        category: FormulaCategory.Expired,
    },
    {
        heading: 'Archived',
        description: 'You may use archived messages for some inspiration.',
        category: FormulaCategory.Archived,
    },
];

export enum FormulaEditOptions {
    message = 'editMessage',
    metadata = 'editMetadata',
}

export interface EmailFieldDataModel {
    label: string;
    isRequired: boolean;
    isMultiLine: boolean;
    hideEmoji: boolean;
    hideCmsPlaceholder: boolean;
    allowVariantCreation: boolean;
}

export enum EmailFieldModifiers {
    required = 'required',
    multiline = 'multiline',
    link = 'link',
    image = 'image',
}

export const EMAIL_LINK_KEYS = ['unsubscribe', 'unsubscribe_preferences'];
export const EMAIL_REQUIRED_KEYS = ['subject', 'pre_header'];

export interface EmailTemplateModel {
    htmlContent: string;
    dynamicFieldsWithModifiers: string[];
    dynamicFieldsWithoutModifiers: string[];
}
export interface TestFormulaData extends FormulaTestFilters {
    contact_id: string;
    template_id?: string;
}
export interface EmailTemplates {
    email_template_id: string;
    template_name: string;
    is_aampe_custom_template: boolean;
}

export type FormulaStartStopType = {
    send_start: string | undefined;
    send_stop: string | undefined;
};

export type EmailLinkInfo = {
    linkValue: string;
    isAnchorTag: boolean;
    textValue: string;
};

export interface FiltersFormValues {
    compound_trigger?: { label: string; value: string };
    cms_type?: { label: string; value: string };
    cms_filters?: CMSFilterCreateModel[];
    tags: { label: string; value: string }[];
    audience_id?: string;
}

export interface AlternateHeaderChildrenType {
    headerName: string;
    variantType?: string;
    id: string;
    kind: ElementKind;
    text?: string;
    datasetId?: string;
    datasetField?: string;
    defaultValue?: string;
    attributeName?: string;
    children?: AlternateHeaderChildrenType[];
}
export interface AlternateHeaderType {
    headerName: string;
    children: AlternateHeaderChildrenType[];
}

export interface MessagesToImproveModel {
    id: number;
    name: string;
    structure?: Descendant[];
    highlight_master_variant_id: string;
    has_write_access?: boolean;
}

export interface FormulaQueryParam {
    surface_id?: string;
}

export interface AttributePlaceholderFormikValues {
    userAttribute: string;
    defaultValue: string;
}

export interface NotificationButtonElement {
    action_id?: string;
    action: string;
    uri?: string;
    text: string;
    id?: string;
    platform?: string;
    formula_id?: number;
}

export interface NotificationButtonFormValues {
    androidButtons: NotificationButtonElement[];
    iosButtons: NotificationButtonElement[];
    iosNotificationCategory?: string;
    androidToggle: boolean;
    iosToggle: boolean;
}

export interface FormulaButtonsUpdateOrCreate {
    buttonPayload: NotificationButtonElement[];
}

export interface SlateNode {
    children: {
        text?: string;
        children?: { text: string }[];
    }[];
}

export interface FormulaTestMessageResponse {
    test_message_content: TestFormulaContent;
}

export interface FormulaTestFilters {
    message_content_id: string;
    formula_id?: number;
    filters?: TestMessageFilter;
}

export interface FormulaTestMessageRequestParams extends FormulaTestFilters {
    contact_ids: string[];
    template_id?: string;
}

export interface FormulaTestMessageRequestParamsV2 extends FormulaTestFilters {
    contact_ids: string[];
    integration_attribute_values?: Record<string, Record<string, string>>;
    template_id?: string;
}

export interface FormulaTestMessageFormValues
    extends FormulaTestMessageRequestParams {
    notification_token?: SearchableSelectType;
    current_state: FormulaTestMessageType;
    users: { id: number | string; name: string }[];
    currentSection: TestFormulaSectionType;
    testMessageContent: TestFormulaContent | null;
    currentStep: TestMessageStep;
    languageId?: string;
    showDataset?: boolean;
    isNextButtonDisabled: boolean;
    componentAttachedLabel: Record<
        string,
        {
            componentTypeId: string;
            labelId: number | null;
        }
    >;
    integration_attribute_values?: Record<string, Record<string, string>>;
}

export interface FormulaLanguagesModel {
    language_ids: string[];
}

export interface UnlabelledFormula {
    id: number;
    name?: string;
    status: FormulaStatus;
    active_state: FormulaState;
    compound_trigger_id?: string;
    surface_id?: string;
}

export interface FormulaBulkUpdate {
    id: number;
    topic_id?: string;
}

export enum FormulaGroupByFilter {
    None = 'none',
    Topic = 'topic',
    Template = 'template',
}
